
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Package item contents
export interface PackageItemContent {
  id: string;
  product_id: string;
  quantity: number;
  is_required: boolean;
  products: {
    id: string;
    name: string;
    price: number;
    image_url?: string;
  };
}

// Unified cart item supporting both products and packages
export interface CartItem {
  id: string;
  item_type: 'product' | 'package';
  quantity: number;
  created_at: string;

  // Product fields
  product_id?: string;
  product_name?: string;
  product_price?: number;
  product_image?: string;

  // Package fields
  package_id?: string;
  package_name?: string;
  package_price?: number;
  package_image?: string;
  package_items?: PackageItemContent[]; // Items in the package
  customizations?: Array<{
    customization_option_id: string;
    selected_value_id?: string;
    custom_value?: string;
    price_modifier: number;
  }>;
}

// Type-safe parameters for adding products
export interface AddProductParams {
  productId: string;
  productName: string;
  productPrice: number;
  productImage: string;
  quantity: number;
}

// Type-safe parameters for adding packages
export interface AddPackageParams {
  packageId: string;
  packageName: string;  // REQUIRED - must be provided to display correctly in cart
  packagePrice: number;  // REQUIRED - must be provided for price calculation
  packageImage?: string; // Optional - image URL for display
  package_items?: PackageItemContent[]; // Optional - items in the package
  quantity: number;
  customizations: Array<{
    customization_option_id: string;
    selected_value_id?: string;
    custom_value?: string;
    price_modifier: number;
  }>;
}

export function usePackageCart(userId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const currentUserId = userId || user?.id;
  const cartKey = currentUserId ? `package-cart-${currentUserId}` : 'package-cart-';

  const getCartItems = (): CartItem[] => {
    const localCart = localStorage.getItem(cartKey);
    return localCart ? JSON.parse(localCart) : [];
  };

  const setCartItems = (items: CartItem[]) => {
    localStorage.setItem(cartKey, JSON.stringify(items));
    queryClient.invalidateQueries({ queryKey: ["package-cart", currentUserId] });
  };

  const itemsQuery = useQuery({
    queryKey: ["package-cart", currentUserId],
    queryFn: getCartItems,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 30000,
    gcTime: 300000,
  });

  const addItemMutation = useMutation({
    mutationFn: async (item: Omit<CartItem, 'id' | 'created_at'>) => {
      const items = getCartItems();
      const existingItemIndex = items.findIndex(
        (i) =>
          (i.item_type === 'product' && i.product_id === item.product_id) ||
          (i.item_type === 'package' && i.package_id === item.package_id)
      );

      if (existingItemIndex !== -1) {
        items[existingItemIndex].quantity += item.quantity;
      } else {
        const newItem: CartItem = {
          ...item,
          id: `${item.item_type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          created_at: new Date().toISOString(),
        };
        items.push(newItem);
      }

      setCartItems(items);
      return items;
    },
    onSuccess: () => {
      toast.success("Item added to cart");
    },
    onError: (error) => {
      console.error("Error adding item to cart:", error);
      toast.error("Failed to add item to cart");
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      let items = getCartItems();
      items = items.filter((item) => item.id !== itemId);
      setCartItems(items);
      return items;
    },
    onSuccess: () => {
      toast.success("Item removed from cart");
    },
    onError: (error) => {
      console.error("Error removing item from cart:", error);
      toast.error("Failed to remove item from cart");
    },
  });

  const updateQuantityMutation = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      const items = getCartItems();
      const itemIndex = items.findIndex((item) => item.id === itemId);

      if (itemIndex !== -1) {
        if (quantity <= 0) {
          items.splice(itemIndex, 1);
        } else {
          items[itemIndex].quantity = quantity;
        }
        setCartItems(items);
      }
      return items;
    },
    onSuccess: () => {
      // No toast for every quantity update to avoid being too noisy
    },
    onError: (error) => {
      console.error("Error updating item quantity:", error);
      toast.error("Failed to update quantity");
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: async () => {
      setCartItems([]);
    },
    onSuccess: () => {
      toast.success("Cart cleared");
    },
    onError: (error) => {
      console.error("Error clearing cart:", error);
      toast.error("Failed to clear cart");
    },
  });

  // Add product with proper typing
  const addProduct = async (params: AddProductParams) => {
    return addItemMutation.mutateAsync({
      item_type: 'product',
      product_id: params.productId,
      product_name: params.productName,
      product_price: params.productPrice,
      product_image: params.productImage,
      quantity: params.quantity,
    });
  };

  // Add package with customizations support
  const addPackage = async (params: AddPackageParams) => {
    return addItemMutation.mutateAsync({
      item_type: 'package',
      package_id: params.packageId,
      package_name: params.packageName,
      package_price: params.packagePrice,
      package_image: params.packageImage,
      package_items: params.package_items, // Include items in the package
      quantity: params.quantity,
      customizations: params.customizations,
    });
  };

  // Compute total item count across all cart items
  const itemCount = (itemsQuery.data || []).reduce((total, item) => total + item.quantity, 0);

  return {
    items: itemsQuery.data || [],
    itemCount,
    isLoading: itemsQuery.isLoading,
    error: itemsQuery.error,
    addItem: addItemMutation.mutateAsync,
    addProduct,
    addPackage,
    removeItem: removeItemMutation.mutateAsync,
    updateQuantity: updateQuantityMutation.mutateAsync,
    clearCart: clearCartMutation.mutateAsync,
    refetch: itemsQuery.refetch,
    isAddingItem: addItemMutation.isPending,
    isRemovingItem: removeItemMutation.isPending,
    isUpdatingQuantity: updateQuantityMutation.isPending,
    isClearingCart: clearCartMutation.isPending,
  };
}
